/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Chat session status
 */
export const SessionSummaryStatus = {
  Success: "success",
  Error: "error",
} as const;
/**
 * Chat session status
 */
export type SessionSummaryStatus = ClosedEnum<typeof SessionSummaryStatus>;

/**
 * Org-scoped summary information for a chat session
 */
export type SessionSummary = {
  /**
   * Chat session duration in seconds
   */
  durationSeconds: number;
  /**
   * Latest log timestamp in Unix nanoseconds (string for JS int64 precision)
   */
  endTimeUnixNano: string;
  /**
   * Chat session ID
   */
  gramChatId: string;
  /**
   * Client or agent surface associated with this chat session
   */
  hookSource?: string | undefined;
  /**
   * Number of LLM completion messages in this chat session
   */
  messageCount: number;
  /**
   * LLM model used in this chat session
   */
  model?: string | undefined;
  /**
   * Project ID that emitted this chat session
   */
  projectId: string;
  /**
   * Earliest log timestamp in Unix nanoseconds (string for JS int64 precision)
   */
  startTimeUnixNano: string;
  /**
   * Chat session status
   */
  status: SessionSummaryStatus;
  /**
   * Chat title, when the session resolves to a named chat
   */
  title?: string | undefined;
  /**
   * Number of tool calls in this chat session
   */
  toolCallCount: number;
  /**
   * Total cost in USD
   */
  totalCost: number;
  /**
   * Total input tokens used
   */
  totalInputTokens: number;
  /**
   * Total output tokens used
   */
  totalOutputTokens: number;
  /**
   * Total tokens used
   */
  totalTokens: number;
  /**
   * User email associated with this chat session
   */
  userEmail?: string | undefined;
};

/** @internal */
export const SessionSummaryStatus$inboundSchema: z.ZodMiniEnum<
  typeof SessionSummaryStatus
> = z.enum(SessionSummaryStatus);

/** @internal */
export const SessionSummary$inboundSchema: z.ZodMiniType<
  SessionSummary,
  unknown
> = z.pipe(
  z.object({
    duration_seconds: z.number(),
    end_time_unix_nano: z.string(),
    gram_chat_id: z.string(),
    hook_source: z.optional(z.string()),
    message_count: z.int(),
    model: z.optional(z.string()),
    project_id: z.string(),
    start_time_unix_nano: z.string(),
    status: SessionSummaryStatus$inboundSchema,
    title: z.optional(z.string()),
    tool_call_count: z.int(),
    total_cost: z.number(),
    total_input_tokens: z.int(),
    total_output_tokens: z.int(),
    total_tokens: z.int(),
    user_email: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "duration_seconds": "durationSeconds",
      "end_time_unix_nano": "endTimeUnixNano",
      "gram_chat_id": "gramChatId",
      "hook_source": "hookSource",
      "message_count": "messageCount",
      "project_id": "projectId",
      "start_time_unix_nano": "startTimeUnixNano",
      "tool_call_count": "toolCallCount",
      "total_cost": "totalCost",
      "total_input_tokens": "totalInputTokens",
      "total_output_tokens": "totalOutputTokens",
      "total_tokens": "totalTokens",
      "user_email": "userEmail",
    });
  }),
);

export function sessionSummaryFromJSON(
  jsonString: string,
): SafeParseResult<SessionSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SessionSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SessionSummary' from JSON`,
  );
}
